import { useQuery } from "@tanstack/react-query"
import { API } from "lib/api"
import { apiName } from "config/api"

const getQAndAInfo = (questionData) => {
    const answer = {}
    const scores = {} 
    const sQuestion = {}

    if (questionData) {
        questionData.質問名
            .forEach((t) => {
                let score = 0
                const questionIndex = []
                questionData[t].質問リスト.forEach((q, i) => {
                    answer[`${q.回答キー}`] = q.回答
                    if (q.回答 === 'はい') score += q.配点
                    if (q.特殊採点 !== '') questionIndex.push(i+1)
                })
                scores[t] = score
                sQuestion[t] = questionIndex.join(' ,')
            })
    }

    return { scores, answer, sQuestion }
}

const getNumberAndTitle = (targetTitles, start=0) =>
    targetTitles.map((t, i) => ({ num: start + i + 1, title: t }))

const getTitles = (data, type) =>
    [...new Set(data.toReversed().flatMap(p => p[`質問名_${type}`]))]

const getPRData = async () => {
    const ret = await API.get(apiName, "/items/getPRAnalysis", {})
    // 完了した回答あり
    ret.hasCompletedAnswer = ret.pranalysis.filter(p => !p.下書き).length > 0
    return ret
}

export const usePRAnalysisAll = () => {
    return useQuery(["pranalysis"], getPRData, {})
}

export const usePRRadarChart = (type, idx) => {
    return useQuery(["pranalysis"], getPRData, {
        select : (data) => { 
            const latest = data.pranalysis[idx - 1]

            // 配点の最大値をグラフの頂点の最大点数とする
            const domain = Math.max(...latest[`質問名_${type}`].map(q => latest[q].合計配点))

            // 指定回数以前で、活動診断テンプレートが同一の物のみ対象とする
            // 但し、質問数が２問以下および下書きのレコードは対象外とする
            const pranalysis = data.pranalysis
                .slice(0, idx)
                .filter(p => p.活動診断テンプレート === latest.活動診断テンプレート)
                .filter(p => p[`質問名_${type}`].length > 2)
                .filter(p => !p.下書き)

            const titles = getNumberAndTitle(getTitles(pranalysis, type), type === '担当者' ? getTitles(pranalysis, '経営者').length : 0)

            if (titles.length === 0) {
                // グラフ表示対象レコードが存在しない場合
                return { titles : [1, 2, 3].map(n => ({ num: n, title: `質問${n}` })), pranalysis : [], domain : 10 }
            }

            return { titles, pranalysis, domain }
        }
    })
}

export const usePRBarChart = (type, targetPrIndex) => {
    return useQuery(['pranalysis'], getPRData, {
        select: (data) => {
            let pranalysis

            if (targetPrIndex >= 0) {
                pranalysis = data.pranalysis[targetPrIndex - 1]
            }
            else {
                pranalysis = Object.keys(data.master).length === 0 || data.master.レコード番号
                    ? data.pranalysis.at(-1) // 下書きなし
                    : data.pranalysis.at(-2) // 下書きあり
            }

            const titles = getNumberAndTitle(pranalysis[`質問名_${type}`], type === '担当者' ? pranalysis.質問名_経営者.length : 0)

            pranalysis.合計点数 = titles.reduce((a, t) => a + pranalysis[t.title].合計点数, 0)
            pranalysis.合計配点 = titles.reduce((a, t) => a + pranalysis[t.title].合計配点, 0)

            return { titles, pranalysis }
        }
    })
}

export const usePRQuestions = (type, targetPrIndex) => {
    return useQuery(['pranalysis'], getPRData, {
        select: (data) => {
            if (targetPrIndex >= 0) { // 過去回答画面
                const questionData = data.pranalysis[targetPrIndex - 1]

                const titles = getNumberAndTitle(questionData[`質問名_${type}`], type === '担当者' ? questionData.質問名_経営者.length : 0)
                const { scores, answer } = getQAndAInfo(questionData)
                return { titles, questionData, isHistory : true, scores, answer, sQuestion : {} }
            } else { 
                const questionData = data.master.レコード番号 ? data.master : data.pranalysis.at(-1) // 下書きなし : 下書きあり

                const titles = getNumberAndTitle(questionData[`質問名_${type}`], type === '担当者' ? questionData.質問名_経営者.length : 0)
                const { scores, answer, sQuestion } = getQAndAInfo(questionData)
                return { titles, questionData, isHistory : false, scores, answer, sQuestion }
            }
        }
    })
}

export const usePRAnalysisHistory = () => {
    return useQuery(['pranalysis'], getPRData, {
        select : (data) =>{
            // 下書き状態の回答は選択肢に含まれない
            const prList = data.pranalysis.filter(p => !p.下書き)

            return prList.map((p, i) => ({ value : i + 1, label : `${p.診断回}回目(${p.PR活動診断日})` }))
        }          
    })  
 }
